import React, { useState } from 'react';
import styled from '@emotion/styled';
import { X, MessageSquare, Send } from 'lucide-react';
import ModalPortal from './ModalPortal';
import { useScrollLock } from '../hooks/useScrollLock';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  color: #6b7280;
  transition: all 0.2s;
  
  &:hover {
    background: #e5e7eb;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  max-height: 60vh;
  overflow-y: auto;
`;

const LoanInfo = styled.div`
  background: #f3f4f6;
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1.5rem;
  border-left: 4px solid #3b82f6;
`;

const LoanId = styled.div`
  font-weight: 600;
  color: #1f2937;
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
`;

const LoanDetails = styled.div`
  color: #6b7280;
  font-size: 0.875rem;
`;

const CommentForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  color: #374151;
  
  &:hover:not(:disabled) {
    background: #e5e7eb;
  }
`;

const SubmitButton = styled(Button)`
  background: #3b82f6;
  border: 1px solid #3b82f6;
  color: white;
  
  &:hover:not(:disabled) {
    background: #2563eb;
    border-color: #2563eb;
  }
`;

const ExistingComments = styled.div`
  margin-bottom: 1.5rem;
`;

const CommentsTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
`;

const CommentItem = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 0.75rem;
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const CommentAuthor = styled.div`
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
`;

const CommentDate = styled.div`
  color: #6b7280;
  font-size: 0.75rem;
`;

const CommentText = styled.div`
  color: #4b5563;
  font-size: 0.875rem;
  line-height: 1.5;
`;

const CommentsModal = ({ 
  isOpen, 
  onClose, 
  loanId = null, 
  loanData = null 
}) => {
  // Lock body scroll when modal is open
  useScrollLock(isOpen);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Comments feature not yet implemented
  const [existingComments] = useState([]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    
    setIsSubmitting(true);
    
    try {
      // Mock API call - in real app, this would save to backend
      console.log('Saving comment:', {
        loanId,
        comment: comment.trim(),
        timestamp: new Date().toISOString()
      });
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Reset form
      setComment('');
      
      // Close modal
      onClose();
      
      // Show success message (in real app, you might use a toast notification)
      alert('Comment saved successfully!');
      
    } catch (error) {
      console.error('Failed to save comment:', error);
      alert('Failed to save comment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setComment('');
      onClose();
    }
  };

  return (
    <ModalPortal isOpen={isOpen}>
      <ModalOverlay>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              <MessageSquare size={20} />
              {loanId ? `Comments for Loan ${loanId}` : 'Add Comment'}
            </ModalTitle>
            <CloseButton onClick={handleClose} disabled={isSubmitting}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>
          
          <ModalBody>
            {loanId && loanData && (
              <LoanInfo>
                <LoanId>Loan ID: {loanId}</LoanId>
                <LoanDetails>
                  {loanData.borrower && `Borrower: ${loanData.borrower}`}
                  {loanData.amount && ` • Amount: ${loanData.amount}`}
                </LoanDetails>
              </LoanInfo>
            )}
            
            {/* Under Construction Message */}
            <div style={{
              textAlign: 'center',
              padding: '3rem 2rem',
              color: '#6b7280'
            }}>
              <div style={{
                fontSize: '3rem',
                marginBottom: '1rem'
              }}>
                🚧
              </div>
              <div style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Under Construction
              </div>
              <div style={{
                fontSize: '0.875rem',
                color: '#6b7280'
              }}>
                Comments feature is currently being developed and will be available soon.
              </div>
            </div>
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    </ModalPortal>
  );
};

export default CommentsModal;
